import * as React from 'react';
import { IFilterControl, IStatusOption, ITypeOption } from '../../FilterSetupPlaceholder';
import { TextFilterControl } from '../TextFilterControl';
import { NumericFilterControl } from '../NumericFilterControl';
import { CurrencyFilterControl } from '../CurrencyFilterControl';
import { DateFilterControl } from '../DateFilterControl';
import { BooleanFilterControl } from '../BooleanFilterControl';
import { FolderStatusFilterControl } from '../FolderStatusFilterControl';
import { ProjectStatusFilterControl } from '../ProjectStatusFilterControl';
import { ActivityStatusFilterControl } from '../ActivityStatusFilterControl';
import { FolderTypeFilterControl } from '../FolderTypeFilterControl';
import { ProjectTypeFilterControl } from '../ProjectTypeFilterControl';
import { ActivityTypeFilterControl } from '../ActivityTypeFilterControl';
import { ProjectFilterControl } from '../ProjectFilterControl';
import { FolderFilterControl } from '../FolderFilterControl';
import { UserFilterControl } from '../UserFilterControl';
import { ResponsibleUserFilterControl } from '../ResponsibleUserFilterControl';

export interface IFilterControlRendererProps {
  filter: IFilterControl;
  onFilterChange: (filter: IFilterControl) => void;
  statusOptions: {
    folder: IStatusOption[];
    project: IStatusOption[];
    activity: IStatusOption[];
  };
  typeOptions: {
    folder: ITypeOption[];
    project: ITypeOption[];
    activity: ITypeOption[];
  };
  fpaTypeCategory?: string;
  disabled?: boolean;
}

// Helper function to extract priority data type from pipe-separated values
// Priority order: specific types (activity_type, project_type, folder_type, etc.) over generic types (text, numeric, etc.)
const getPriorityDataType = (dataType: string): string => {
  if (!dataType || !dataType.includes('|')) {
    return dataType;
  }

  // Handle folder-{fpaTypeCategory}|text pattern - extract 'folder' as the type
  if (dataType.startsWith('folder-') && dataType.includes('|')) {
    return 'folder';
  }

  const types = dataType.split('|').map(t => t.trim());
  var index:number = ['dimension1', 'dimension2', 'dimension3'].includes(types[0]) ? 1 : 0;
  return types[index];
};

// Helper function to extract fpaTypeCategory from folder filter pattern
// Pattern: 'folder-{fpaTypeCategory}|text'
// Example: 'folder-com.atollon.project.fpatypecategory.supplier|text'
// Returns: 'com.atollon.project.fpatypecategory.supplier' or undefined if pattern doesn't match
const extractFpaTypeCategory = (dataType: string): string | undefined => {
  if (!dataType || !dataType.startsWith('folder-') || !dataType.includes('|')) {
    return undefined;
  }
  
  const match = dataType.match(/^folder-(.+?)\|/);
  return match ? match[1] : undefined;
};

export const FilterControlRenderer = React.forwardRef<HTMLDivElement, IFilterControlRendererProps>(
  (props, forwardedRef) => {
    const { filter, onFilterChange, statusOptions, typeOptions, fpaTypeCategory, disabled } = props;
    const data_type = getPriorityDataType(filter.dataType);
    
    switch (data_type) {
      case 'text':
      case 'dimension1Name':
      case 'dimension2Name':
      case 'dimension3Name':
        return <TextFilterControl ref={forwardedRef} filter={filter} onFilterChange={onFilterChange} />;
      case 'numeric':
        return <NumericFilterControl ref={forwardedRef} filter={filter} onFilterChange={onFilterChange} />;
      case 'currency':
        return <CurrencyFilterControl ref={forwardedRef} filter={filter} onFilterChange={onFilterChange} />;
      case 'date':
        return <DateFilterControl ref={forwardedRef} filter={filter} onFilterChange={onFilterChange} />;
      case 'boolean':
        return <BooleanFilterControl ref={forwardedRef} filter={filter} onFilterChange={onFilterChange} />;
      case 'folder_status':
        return (
          <FolderStatusFilterControl
            ref={forwardedRef}
            filter={filter}
            onFilterChange={onFilterChange}
            options={statusOptions.folder}
          />
        );
      case 'project_status':
        return (
          <ProjectStatusFilterControl
            ref={forwardedRef}
            filter={filter}
            onFilterChange={onFilterChange}
            options={statusOptions.project}
          />
        );
      case 'activity_status':
        return (
          <ActivityStatusFilterControl
            ref={forwardedRef}
            filter={filter}
            onFilterChange={onFilterChange}
            options={statusOptions.activity}
          />
        );
      case 'folder_type':
        return (
          <FolderTypeFilterControl
            ref={forwardedRef}
            filter={filter}
            onFilterChange={onFilterChange}
            options={typeOptions.folder}
          />
        );
      case 'project_type':
        return (
          <ProjectTypeFilterControl
            ref={forwardedRef}
            filter={filter}
            onFilterChange={onFilterChange}
            options={typeOptions.project}
            fpaTypeCategory={fpaTypeCategory}
          />
        );
      case 'activity_type':
        return (
          <ActivityTypeFilterControl
            ref={forwardedRef}
            filter={filter}
            onFilterChange={onFilterChange}
            options={typeOptions.activity}
            fpaTypeCategory={fpaTypeCategory}
          />
        );
      case 'project':
        return (
          <ProjectFilterControl
            ref={forwardedRef}
            filter={filter}
            onFilterChange={onFilterChange}
          />
        );
      case 'folder': {
        // Check if this is a folder filter with fpaTypeCategory pattern
        const fpaTypeCategory = extractFpaTypeCategory(filter.dataType);
        return (
          <FolderFilterControl
            ref={forwardedRef}
            filter={filter}
            onFilterChange={onFilterChange}
            fpaTypeCategory={fpaTypeCategory}
          />
        );
      }
      case 'user':
        return (
          <UserFilterControl
            ref={forwardedRef}
            filter={filter}
            onFilterChange={onFilterChange}
          />
        );
      case 'filter.user':
        return (
          <ResponsibleUserFilterControl
            ref={forwardedRef}
            filter={filter}
            onFilterChange={onFilterChange}
            disabled={disabled}
          />
        );
      default:
        return <div>Unsupported filter type: {filter.dataType}</div>;
    }
  }
);

FilterControlRenderer.displayName = 'FilterControlRenderer';

